import { Fragment, useState } from 'react';
import type { Deal, TabState } from '../../types';
import { buildSCList, PRODUCT_BUCKETS, bucketProducts, matchesCQFilter } from '../../utils/filter';
import { fmtG } from '../../utils/format';
import { sumUniqueBy, validAges, isAged, ageSeverity, formatAge } from '../../utils/deals';
import { useDeals } from '../../context/DealsContext';
import { MetricCard } from '../ui/MetricCard';
import { MedianAgeCard } from '../ui/MedianAgeCard';
import { GnarrInfluencedCard } from '../ui/GnarrInfluencedCard';
import { SectionHeader } from '../ui/SectionHeader';
import { getURL } from '../../utils/deals';
interface Props {
  state: TabState;
}
function SCMatrix({
  region,
  sub,
  cqState,
  data
}: {
  region: string;
  sub: string;
  cqState: TabState;
  data: Deal[];
}) {
  const ipData = data.filter(d => d.status === 'In Progress' && (region === 'Global' || d.region === region) && (!sub || sub === 'All' || d.sub_region === sub) && matchesCQFilter(d, cqState));
  const COLS = [...PRODUCT_BUCKETS.map(b => b.label), 'Other'];
  const scMap: Record<string, Record<string, number>> = {};
  const seenDR: Record<string, number> = {};
  for (const d of ipData) {
    if (!scMap[d.sc]) scMap[d.sc] = {};
    const bkts = bucketProducts(d.products || '');
    for (const bkt of bkts) {
      const uniq = d.sc + '|' + bkt + '|' + d.id;
      if (seenDR[uniq]) continue;
      seenDR[uniq] = 1;
      scMap[d.sc][bkt] = (scMap[d.sc][bkt] || 0) + 1;
    }
  }
  const scRows = Object.entries(scMap).map(([name, bkts]) => ({
    name: name.split(' ')[0],
    full: name,
    bkts,
    total: Object.values(bkts).reduce((a, b) => a + b, 0)
  })).sort((a, b) => b.total - a.total);
  const colTotals: Record<string, number> = {};
  for (const c of COLS) colTotals[c] = 0;
  for (const row of scRows) {
    for (const c of COLS) colTotals[c] += row.bkts[c] || 0;
  }
  const grandTotal = Object.values(colTotals).reduce((a, b) => a + b, 0);
  const activeCols = COLS.filter(c => colTotals[c] > 0);
  if (scRows.length === 0 || grandTotal === 0) {
    return <tr data-id="element-205" data-mp="3l4mpzssztb3g">
        <td colSpan={2} style={{
        padding: '12px 10px',
        color: '#8892a0',
        fontSize: 10,
        fontStyle: 'italic'
      }} data-id="element-206" data-mp="3l4d6mo2ge26q">
          No in-progress deals match the current filter.
        </td>
      </tr>;
  }
  return <>
      <thead data-id="element-207" data-mp="3u49skhxgkmy3">
        <tr data-id="element-208" data-mp="3u51ef11976b6">
          <th data-id="element-209" data-mp="3u3gt820twzpz">SC</th>
          {activeCols.map(c => <th key={c} data-id="element-210" data-mp="2xfu0of6vm1ht">{c}</th>)}
          <th data-id="element-211" data-mp="2xglp96222yyl">Total</th>
        </tr>
      </thead>
      <tbody data-id="element-212" data-mp="32eetvunpnkwr">
        {scRows.map(row => <tr key={row.full} data-id="element-213" data-mp="32f6ph5hb5in1">
            <td title={row.full} data-id="element-214" data-mp="32car5g1jsry2">{row.name}</td>
            {activeCols.map(c => <td key={c} data-id="element-215" data-mp="36ep6vr9x7bl4">
                {(row.bkts[c] || 0) > 0 ? <span className="mx-val" data-id="element-216" data-mp="3b7qo45xlnn7v">{row.bkts[c]}</span> : <span style={{
            color: '#d1d5db'
          }} data-id="element-217" data-mp="2ep2jirgnd6sv">—</span>}
              </td>)}
            <td data-id="element-218" data-mp="2iqebh3mzu448"><span className="mx-val" style={{
            background: '#e0e7ff',
            color: '#3730a3'
          }} data-id="element-219" data-mp="2iqe9wz7chw24">{row.total}</span></td>
          </tr>)}
      </tbody>
      <tfoot data-id="element-220" data-mp="2b5ljve99tkar">
        <tr data-id="element-221" data-mp="2b63qn4tk7gwx">
          <td data-id="element-222" data-mp="2b4iwvhf2k204">Total</td>
          {activeCols.map(c => <td key={c} data-id="element-223" data-mp="2693lcw4jai6v"><span style={{
            fontWeight: 700,
            color: '#1d4ed8'
          }} data-id="element-224" data-mp="268bpdk70yig2">{colTotals[c] || 0}</span></td>)}
          <td data-id="element-225" data-mp="224dop521wzkw"><span style={{
            fontWeight: 700,
            color: '#3730a3'
          }} data-id="element-226" data-mp="224dq99hp97n0">{grandTotal}</span></td>
        </tr>
      </tfoot>
    </>;
}
export function ODPanel({
  state
}: Props) {
  const [expandedSC, setExpandedSC] = useState<string | null>(null);
  const {
    deals
  } = useDeals();
  const scData = buildSCList(state.r, state.sub, state, deals);
  const allIP = deals.filter(d => {
    if (d.status !== 'In Progress') return false;
    if (state.r !== 'Global' && d.region !== state.r) return false;
    if (state.sub && state.sub !== 'All' && d.sub_region !== state.sub) return false;
    if (!matchesCQFilter(d, state)) return false;
    return true;
  });
  const scs = new Set(allIP.map(d => d.sc)).size;

  // oppGnarr includes ALL statuses (not just IP), matching region/sub/CQ
  const allForGnarr = deals.filter(d => {
    if (state.r !== 'Global' && d.region !== state.r) return false;
    if (state.sub && state.sub !== 'All' && d.sub_region !== state.sub) return false;
    if (!matchesCQFilter(d, state)) return false;
    return true;
  });
  const oppGnarr = sumUniqueBy(allForGnarr, d => d.opp_gnarr);
  const ages = validAges(allIP);
  const maxIP = Math.max(...scData.map(s => s.ip), 1);
  const maxClosed = Math.max(...scData.map(s => s.closed), 1);
  return <div data-id="element-227" data-mp="2pwl1s3il77ex">
      {/* Metrics */}
      <div className="mrow" data-id="element-228" data-mp="2ptgqkm6pfber">
        <MetricCard label="In-progress requests" value={allIP.length} sub={`${scs} SCs active`} colorClass="c-blue" data-id="element-229" data-mp="2bwk0wo2bnzzg" />
        <MetricCard label="Requests / person" value={scData.length ? +(allIP.length / scData.length).toFixed(1) : 0} sub="across active SCs" colorClass="c-purple" data-id="element-230" data-mp="2gw6eiym2bi8s" />
        <MedianAgeCard ages={ages} data-id="element-231" data-mp="f3qj42qop9xc" />
        <GnarrInfluencedCard value={oppGnarr} colorClass="c-green" data-id="element-232" data-mp="f2pb674yt3ju" />
      </div>

      {/* SC workload table */}
      <SectionHeader title="SC workload — deals (click row to expand)" data-id="element-233" data-mp="1zmkkld5qrqdl" />
      <div className="tw" data-id="element-234" data-mp="1unhh3x5ets7q">
        <table className="tbl" data-id="element-235" data-mp="1uo9cp7z0bpy0">
          <thead data-id="element-236" data-mp="1ukvaq0u99ir3">
            <tr data-id="element-237" data-mp="29bccjlasyaoi">
              <th style={{
              width: '26%'
            }} data-id="element-238" data-mp="29bue8xg8hh3t">SC Name</th>
              <th style={{
              width: '18%'
            }} data-id="element-239" data-mp="29dfaqsm3za4b">In-progress</th>
              <th style={{
              width: '18%'
            }} data-id="element-240" data-mp="24k400ge6e974">Closed</th>
              <th style={{
              width: '13%'
            }} data-id="element-241" data-mp="2icafs1df2e61">Avg age</th>
              <th style={{
              width: '13%'
            }} data-id="element-242" data-mp="2iapkuan6wt7n">Median age</th>
              <th style={{
              width: '12%'
            }} data-id="element-243" data-mp="2ddpfk1y5zuhl">GNARR (In-Progress)</th>
            </tr>
          </thead>
          <tbody data-id="element-244" data-mp="2sb01644iugr1">
            {scData.map((p, rowPosition) => <Fragment key={`${p.n}-${rowPosition}`} data-id="element-245" data-mp="2s8422cgxtm10" data-mpi={rowPosition}>
                <tr key={p.n} onClick={() => setExpandedSC(expandedSC === p.n ? null : p.n)} data-id="element-246" data-mp="2ncf062hg3t1j">
                  <td data-id="element-247" data-mp="xxgvu8491tv6">
                    <span className="name-link" data-id="element-248" data-mp="xwyzlbhl7fn9">
                      {p.n}
                      <span className={`chev${expandedSC === p.n ? ' open' : ''}`} data-id="element-249" data-mp="sye0xghzh40x">▾</span>
                    </span>
                  </td>
                  <td data-id="element-250" data-mp="1inkvetk9dr1p">
                    <div className="pbg" style={{
                  width: 70
                }} data-id="element-251" data-mp="1mmtkvm89n2rg">
                      <div className="pf" style={{
                    width: `${Math.round(p.ip / maxIP * 100)}%`
                  }} data-id="element-252" data-mp="1mnd1mzm6jl3w" />
                    </div>
                    <strong data-id="element-253" data-mp="1wl9fhz6i4u5p">{p.ip}</strong>
                  </td>
                  <td data-id="element-254" data-mp="1wmkhbtgdzru6">
                    <div className="pbg" style={{
                  width: 70
                }} data-id="element-255" data-mp="1wiwo6veut7fz">
                      <div className="pf" style={{
                    width: `${Math.round(p.closed / maxClosed * 100)}%`,
                    background: '#34d399'
                  }} data-id="element-256" data-mp="21jufpylr7du5" />
                    </div>
                    <strong data-id="element-257" data-mp="2acxlrlu2koer">{p.closed}</strong>
                  </td>
                  <td style={{
                color: p.avgAge > 90 ? '#f87171' : p.avgAge > 40 ? '#d4a017' : '#344054',
                fontWeight: p.avgAge > 40 ? 600 : 400
              }} data-id="element-258" data-mp="2aequd6o8tipe">
                    {p.avgAge}d
                  </td>
                  <td data-id="element-259" data-mp="2k90p2v0nqt3a">{p.med}d</td>
                  <td data-id="element-260" data-mp="2p9ezuktn8h50">{fmtG(p.gnarr)}</td>
                </tr>
                {expandedSC === p.n && <tr key={`exp-${p.n}`} data-id="element-261" data-mp="dmuziququcrr">
                    <td colSpan={6} style={{
                padding: 0
              }} data-id="element-262" data-mp="dnwcx1z8fbcn">
                      <div className="exp-inner" data-id="element-263" data-mp="8n8j68jmcgeq">
                        <div style={{
                    fontSize: 9,
                    fontWeight: 700,
                    color: '#667085',
                    marginBottom: 6,
                    textTransform: 'uppercase',
                    letterSpacing: '.05em'
                  }} data-id="element-264" data-mp="8put2etzf804">
                          In-progress book — {p.n} ({p.ip} deals · {fmtG(p.gnarr)} GNARR)
                        </div>
                        <div className="deal-grid" data-id="element-265" data-mp="wcb1lim2udaa">
                          {p.deals.map(d => <div key={d.id} className="deal-chip" data-id="element-266" data-mp="rernqjtak2iv">
                              <div className="dc-id" data-id="element-267" data-mp="mm7ygpl8riy8">{d.id}</div>
                              <div className="dc-name" data-id="element-268" data-mp="ho6g66vn3nnt">{d.account.substring(0, 35)}</div>
                              <div style={{
                        fontSize: 9,
                        color: '#8892a0',
                        marginTop: 3
                      }} data-id="element-269" data-mp="hmc3tgyfp3vc"><a href={getURL(d)} target="_blank" rel="noopener noreferrer" data-id="element-270" data-mp="1f4c5izdpktqk">{d.requestName.substring(0, 35)}</a></div>
                              <div className="dc-tags" data-id="element-271" data-mp="1ab0z2zct6ez6">
                                <span className={`badge ${ageSeverity(d.age) === 'high' ? 'b-red' : ageSeverity(d.age) === 'medium' ? 'b-amber' : 'b-gray'}`} data-id="element-272" data-mp="iohdvsufg6ps">{formatAge(d.age)}</span>
                                <span className="badge b-blue" data-id="element-273" data-mp="9pdvkr333vcc">{fmtG(d.gnarr)}</span>
                                <span className="badge b-purple" style={{
                          fontSize: 8
                        }} data-id="element-274" data-mp="en5x8ctj6vtq">{d.close_q}</span>
                                {isAged(d) && <span className="badge b-red" data-id="element-275" data-mp="12ebcaprndk2z">⚠ aged</span>}
                              </div>
                              <div style={{
                        fontSize: 9,
                        color: '#8892a0',
                        marginTop: 3
                      }} data-id="element-276" data-mp="16dk7lz2c5q0n">
                                {d.sub_region} · {d.offering.substring(0, 26)}
                              </div>
                            </div>)}
                        </div>
                        {p.closedDeals.length > 0 && <>
                            <div style={{
                      fontSize: 9,
                      fontWeight: 700,
                      color: '#059669',
                      margin: '12px 0 6px',
                      textTransform: 'uppercase',
                      letterSpacing: '.05em'
                    }} data-id="element-277" data-mp="3i5fokwpmma42">
                              Closed — {p.n} ({p.closed} deals)
                            </div>
                            <div className="deal-grid" data-id="element-278" data-mp="3jll1rru621u8">
                              {p.closedDeals.map(d => <div key={d.id} className="deal-chip" style={{
                        borderColor: '#d1fae5'
                      }} data-id="element-279" data-mp="3elysfthgl5qp">
                                  <div className="dc-id" data-id="element-280" data-mp="2qw4fzd11xj6z">{d.id}</div>
                                  <div className="dc-name" data-id="element-281" data-mp="2mvas2jnm04e3">{d.account.substring(0, 35)}</div>
                                  <div style={{
                          fontSize: 9,
                          color: '#8892a0',
                          marginTop: 3
                        }} data-id="element-282" data-mp="2mthk9119fe4i"> <a href={getURL(d)} target="_blank" rel="noopener noreferrer" data-id="element-283" data-mp="30nh7g3ixu7ch">{d.requestName.substring(0, 40)}</a></div>
                                  <div className="dc-tags" data-id="element-284" data-mp="2vu5x3sex38ft">
                                    <span className="badge" style={{
                            background: '#d1fae5',
                            color: '#065f46'
                          }} data-id="element-285" data-mp="2853fulo73hlu">✓ Closed</span>
                                    <span className="badge b-blue" data-id="element-286" data-mp="2358raorw743y">{fmtG(d.gnarr)}</span>
                                    <span className="badge b-purple" style={{
                            fontSize: 8
                          }} data-id="element-287" data-mp="9lo42rxl669k">{d.close_q}</span>
                                  </div>
                                  <div style={{
                          fontSize: 9,
                          color: '#8892a0',
                          marginTop: 3
                        }} data-id="element-288" data-mp="3vt4hn8toouag">
                                    {d.sub_region} · {d.offering.substring(0, 26)}
                                  </div>
                                </div>)}
                            </div>
                          </>}
                      </div>
                    </td>
                  </tr>}
              </Fragment>)}
          </tbody>
        </table>
      </div>

      {/* Solution × SC matrix */}
      <SectionHeader title="Solution × SC matrix — product concentration" data-id="element-289" data-mp="342hv5wwkyyte" />
      <div className="tw" data-id="element-290" data-mp="344kk0dlzylwj">
        <div className="sub" data-id="element-291" data-mp="2z3n6wffugi1w">
          In-progress deals by product bucket per SC. Reveals single-threading and coverage gaps.
        </div>
        <div style={{
        overflowX: 'auto'
      }} data-id="element-292" data-mp="1ekktpggeh2c1">
          <table className="tbl" id="od-matrix-tbl" style={{
          minWidth: 500
        }} data-id="element-293" data-mp="1jkffx6p8d3qr">
            <SCMatrix region={state.r} sub={state.sub} cqState={state} data={deals} data-id="element-294" data-mp="1jmjtkgcxlh48" />
          </table>
        </div>
      </div>
    </div>;
}